"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { FirebaseApp } from "firebase/app";
import { Database } from "firebase/database";
import { Auth, User } from "firebase/auth";
import { useSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import type { Session } from "next-auth";
import { toast } from "react-toastify";
import { getFirebaseApp, getFirebaseAuth, getFirebaseDatabase } from "@/app/lib/firebaseClient";
import { signInWithCustomToken } from "firebase/auth";

interface ChatProviderContextType {
  app: FirebaseApp | null;
  database: Database | null;
  auth: Auth | null;
  user: User | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
}

const ChatProviderContext = createContext<ChatProviderContextType>({
  app: null,
  database: null,
  auth: null,
  user: null,
  isAuthenticated: false,
  isInitializing: true,
});

export const useFirebase = () => useContext(ChatProviderContext);

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [app, setApp] = useState<FirebaseApp | null>(null);
  const [database, setDatabase] = useState<Database | null>(null);
  const [auth, setAuth] = useState<Auth | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  
  const { data: session } = useSession();
  const pathname = usePathname();
  
  // Check if current path is a chat page
  const isChatPage = pathname && (pathname === '/chat' || pathname.startsWith('/chat') || 
                              pathname === '/driver/chat' || pathname.startsWith('/driver/chat'));

  // Initialize Firebase only on chat pages
  useEffect(() => {
    // Skip Firebase initialization if not on a chat page
    if (!isChatPage) {
      setIsInitializing(false);
      return;
    }
    
    try {
      const firebaseApp = getFirebaseApp();
      const db = getFirebaseDatabase();
      const authInstance = getFirebaseAuth();

      setApp(firebaseApp);
      setDatabase(db);
      setAuth(authInstance);
      
      // Listen for auth state changes
      const unsubscribe = authInstance.onAuthStateChanged((currentUser: User | null) => {
        setUser(currentUser);
        setIsAuthenticated(!!currentUser);
        setIsInitializing(false);
        
        // If no Firebase user exists but we have a NextAuth session, fetch a token
        if (!currentUser && session) {
          fetchAndSignInWithFirebaseToken(authInstance);
        }
      });
      
      return () => unsubscribe();
    } catch (error) {
      console.error("Error initializing Firebase:", error);
      toast.error("Failed to initialize Firebase");
      setIsInitializing(false);
    }
  }, [session, isChatPage]);
  
  // Function to fetch Firebase token and sign in
  const fetchAndSignInWithFirebaseToken = async (authInstance: Auth) => {
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/chat/firebase-token`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch Firebase token: ${response.status}`);
      }
      
      const data = await response.json();
      const firebaseToken = data.firebaseToken;
      
      if (firebaseToken) {
        const userCredential = await signInWithCustomToken(authInstance, firebaseToken);
        setUser(userCredential.user);
        setIsAuthenticated(true);
      } else {
        throw new Error("No Firebase token returned from API");
      }
    } catch (error) {
      console.error("Error signing in with Firebase token:", error);
      toast.error("Failed to authenticate with Firebase");
    }
  };  
  const value = {
    app,
    database,
    auth,
    user,
    isAuthenticated,
    isInitializing,
  };
  
  return (
    <ChatProviderContext.Provider value={value}>
      {children}
    </ChatProviderContext.Provider>
  );
};
